import React, { useState, useMemo } from 'react';
import { tasks, teamMembers, COGNITIVE_LANES, COGNITIVE_LEVELS } from '../mock';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Users, GitBranch, Layers, Calendar, ChevronRight, X, ChevronDown,
  Zap, Target, Clock, CheckCircle2, MessageSquare, FileText,
  TrendingUp, Activity, Sparkles, AlertCircle, ArrowUpRight,
  ListChecks, BarChart3, UserCircle, CircleDot, Info, Crown,
  Edit3, Plus, CalendarDays, Flag, Pencil
} from 'lucide-react';
import { DESIGN } from '../components/DesignSystem';
import NewCollaborationModal from '../components/NewCollaborationModal';

// ================================================
// GET HELPERS
// ================================================
const getOwner = (ownerId) => teamMembers.find(tm => tm.id === ownerId);
const getContributors = (contributorIds) => contributorIds?.map(id => teamMembers.find(tm => tm.id === id)).filter(Boolean) || [];
const getLane = (laneId) => COGNITIVE_LANES.find(l => l.id === laneId);
const getLevel = (levelId) => COGNITIVE_LEVELS.find(l => l.id === levelId);
const getLevelColor = (level) => DESIGN.levels[level]?.color || DESIGN.colors.textMuted;

// Generate avatar gradient
const getAvatarGradient = (name) => {
  const gradients = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
    'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)',
    'linear-gradient(135deg, #13547a 0%, #80d0c7 100%)',
  ];
  const hash = name?.split('').reduce((a, b) => a + b.charCodeAt(0), 0) || 0;
  return gradients[hash % gradients.length];
};

// Generate multi-lane gradient for collab border
const getMultiLaneGradient = (primaryLane, supportingLanes = []) => {
  const allLanes = [primaryLane, ...supportingLanes].filter(Boolean);
  if (allLanes.length === 1) {
    return `linear-gradient(180deg, ${allLanes[0]?.color} 0%, ${allLanes[0]?.color}CC 100%)`;
  }
  const stops = allLanes.map((lane, idx) => {
    const percentage = (idx / (allLanes.length - 1)) * 100;
    return `${lane?.color} ${percentage}%`;
  }).join(', ');
  return `linear-gradient(180deg, ${stops})`;
};

// Get capacity status color
const getCapacityColor = (member) => {
  const load = member?.active_tasks || Math.floor(Math.random() * 10) + 2;
  const max = member?.max_task_load || 10;
  const ratio = load / max;
  if (ratio >= 0.9) return DESIGN.colors.error;
  if (ratio >= 0.7) return DESIGN.colors.amber;
  return DESIGN.colors.success;
};

// ================================================
// PREMIUM ELEVATED STAT CARD
// ================================================
const PremiumStatCard = ({ icon: Icon, label, value, color, index }) => (
  <div 
    className="relative rounded-2xl p-7 overflow-hidden transition-all hover:shadow-2xl hover:-translate-y-1.5 cursor-pointer group"
    style={{
      backgroundColor: DESIGN.colors.card,
      boxShadow: DESIGN.shadows.lg,
      animation: `fadeInUp 0.5s ease-out ${index * 100}ms forwards`,
      opacity: 0
    }}
    data-testid={`stat-card-${index}`}
  >
    {/* Enhanced gradient background */}
    <div 
      className="absolute top-0 right-0 w-40 h-40 opacity-15 group-hover:opacity-25 transition-all duration-500"
      style={{ 
        background: `radial-gradient(circle at top right, ${color} 0%, transparent 70%)`,
        transform: 'translate(20%, -20%)'
      }}
    />
    <div 
      className="absolute bottom-0 left-0 w-24 h-24 opacity-10"
      style={{ background: `radial-gradient(circle at bottom left, ${color} 0%, transparent 70%)` }}
    />
    
    <div className="relative">
      <div 
        className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300"
        style={{ 
          backgroundColor: `${color}15`, 
          boxShadow: `0 0 25px ${color}35`,
          border: `2px solid ${color}30`
        }}
      >
        <Icon className="w-7 h-7" style={{ color }} />
      </div>
      <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: DESIGN.colors.textMuted }}>
        {label}
      </p>
      <p className="text-5xl font-black tracking-tight" style={{ color: DESIGN.colors.textPrimary }}>{value}</p>
    </div>
  </div>
);

// ================================================
// PREMIUM AVATAR WITH CAPACITY RING
// ================================================
const PremiumAvatar = ({ member, isOwner, size = 'md', showCapacity = true, className = '' }) => {
  const sizes = {
    sm: { avatar: 'w-9 h-9 text-xs', ring: 2, star: 'w-4 h-4 -top-1 -right-1' },
    md: { avatar: 'w-12 h-12 text-sm', ring: 3, star: 'w-5 h-5 -top-1 -right-1' },
    lg: { avatar: 'w-16 h-16 text-base', ring: 4, star: 'w-6 h-6 -top-1 -right-1' }
  };
  const s = sizes[size];
  const capacityColor = getCapacityColor(member);
  
  return (
    <div className={`relative flex-shrink-0 ${className}`}>
      {/* Capacity ring */}
      {showCapacity && (
        <div 
          className={`absolute inset-0 rounded-xl`}
          style={{ 
            border: `${s.ring}px solid ${capacityColor}`,
            boxShadow: `0 0 10px ${capacityColor}40`
          }}
        />
      )}
      <div 
        className={`${s.avatar} rounded-xl flex items-center justify-center font-bold`}
        style={{ 
          background: getAvatarGradient(member?.name),
          color: 'white',
          margin: showCapacity ? `${s.ring}px` : 0
        }}
      >
        {member?.avatar}
      </div>
      {isOwner && (
        <div 
          className={`absolute ${s.star} rounded-full flex items-center justify-center z-10`}
          style={{ backgroundColor: '#F59E0B', boxShadow: '0 2px 8px rgba(245, 158, 11, 0.6)' }}
        >
          <Crown className="w-2.5 h-2.5 text-white" />
        </div>
      )}
    </div>
  );
};

// ================================================
// OVERLAPPING AVATAR CLUSTER
// ================================================
const AvatarCluster = ({ members, maxDisplay = 4, size = 'sm' }) => {
  const displayed = members.slice(0, maxDisplay);
  const remaining = members.length - maxDisplay;
  
  return (
    <div className="flex items-center">
      <div className="flex -space-x-2">
        {displayed.map((member, idx) => (
          <PremiumAvatar 
            key={member?.id || idx} 
            member={member} 
            isOwner={idx === 0} 
            size={size}
            showCapacity={true}
            className="hover:z-10 hover:scale-110 transition-all"
          />
        ))}
      </div>
      {remaining > 0 && (
        <span 
          className="ml-2 text-xs font-bold px-2.5 py-1.5 rounded-xl"
          style={{ 
            backgroundColor: DESIGN.colors.background, 
            color: DESIGN.colors.textMuted,
            border: `1px solid ${DESIGN.colors.border}`
          }}
        >
          +{remaining}
        </span>
      )}
    </div>
  );
};

// ================================================
// PREMIUM LANE PILLS - ELEVATED
// ================================================
const PrimaryLanePill = ({ lane }) => (
  <div 
    className="inline-flex items-center gap-2.5 rounded-xl px-5 py-3"
    style={{ 
      background: `linear-gradient(135deg, ${lane?.color}25 0%, ${lane?.color}15 100%)`,
      border: `2px solid ${lane?.color}`,
      boxShadow: `0 0 20px ${lane?.color}30, inset 0 1px 0 rgba(255,255,255,0.1)`
    }}
  >
    <div 
      className="w-4 h-4 rounded-full animate-pulse"
      style={{ backgroundColor: lane?.color, boxShadow: `0 0 12px ${lane?.color}80` }}
    />
    <span className="font-bold text-base" style={{ color: DESIGN.colors.textPrimary }}>
      {lane?.name}
    </span>
    <span 
      className="text-xs font-black px-2.5 py-1 rounded-lg"
      style={{ backgroundColor: lane?.color, color: 'white' }}
    >
      Primary
    </span>
  </div>
);

const SupportingLanePill = ({ lane }) => (
  <div 
    className="inline-flex items-center gap-2 rounded-xl px-3.5 py-2"
    style={{ 
      backgroundColor: DESIGN.colors.background,
      border: `1px solid ${DESIGN.colors.border}`,
      borderLeft: `4px solid ${lane?.color}`
    }}
  >
    <div 
      className="w-2.5 h-2.5 rounded-full"
      style={{ backgroundColor: lane?.color }}
    />
    <span className="text-sm font-medium" style={{ color: DESIGN.colors.textSecondary }}>
      {lane?.name}
    </span>
  </div>
);

// ================================================
// LANE COMPOSITION DOTS (Compact)
// ================================================
const LaneCompositionDots = ({ primaryLane, supportingLanes = [], size = 'sm' }) => {
  const allLanes = [primaryLane, ...supportingLanes].filter(Boolean);
  const dotSize = size === 'sm' ? 'w-2.5 h-2.5' : 'w-3 h-3';
  
  return (
    <div className="flex items-center gap-1">
      {allLanes.map((lane, idx) => (
        <div 
          key={lane?.id || idx}
          className={`${dotSize} rounded-full`}
          style={{ 
            backgroundColor: lane?.color,
            boxShadow: idx === 0 ? `0 0 8px ${lane?.color}60` : 'none'
          }}
          title={lane?.name}
        />
      ))}
    </div>
  );
};

// ================================================
// COLLAB BADGE
// ================================================
const CollabBadge = ({ size = 'sm' }) => {
  const sizes = {
    sm: 'text-xs px-2 py-1 gap-1',
    md: 'text-sm px-3 py-1.5 gap-1.5'
  };
  
  return (
    <span 
      className={`inline-flex items-center font-bold rounded-lg ${sizes[size]}`}
      style={{ 
        background: 'linear-gradient(135deg, #8B5CF6 0%, #A855F7 50%, #C084FC 100%)',
        color: 'white',
        boxShadow: '0 2px 8px rgba(139, 92, 246, 0.4)'
      }}
    >
      <Users className={size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
      Collab
    </span>
  );
};

// ================================================
// COLLABORATION DETAIL MODAL - ELEVATED
// ================================================
const CollabDetailModal = ({ task, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  
  if (!task) return null;
  
  const owner = getOwner(task.owner_id);
  const contributors = getContributors(task.contributors);
  const primaryLane = getLane(task.primary_lane);
  const supportingLanes = task.supporting_lanes?.map(lId => getLane(lId)).filter(Boolean) || [];
  const levelData = getLevel(task.required_level);
  const levelColor = getLevelColor(task.required_level);
  
  // Rich mock data for detail view
  const mockProgress = Math.floor(Math.random() * 40) + 50;
  const mockMilestones = [
    { name: 'Research & Discovery', status: 'completed', date: 'Jan 15', assignee: owner, description: 'User research and requirements gathering' },
    { name: 'Design Phase', status: 'completed', date: 'Jan 28', assignee: contributors[0] || owner, description: 'UI/UX design and prototyping' },
    { name: 'Development Sprint', status: 'in_progress', date: 'Feb 10', progress: 65, assignee: contributors[1] || owner, description: 'Core feature implementation' },
    { name: 'Testing & QA', status: 'pending', date: 'Feb 20', assignee: contributors[0] || owner, description: 'Quality assurance and bug fixes' },
    { name: 'Launch', status: 'pending', date: 'Mar 1', assignee: owner, description: 'Production deployment' }
  ];
  
  // Expanded activity data grouped by date
  const mockActivity = {
    today: [
      { user: owner, action: 'updated project scope and timeline', time: '2 hours ago', type: 'edit' },
      { user: contributors[0], action: 'completed design mockups review', time: '4 hours ago', type: 'complete' },
      { user: owner, action: 'added 2 new team members', time: '5 hours ago', type: 'add' },
    ],
    yesterday: [
      { user: contributors[1] || owner, action: 'scheduled team sync for Friday', time: 'Yesterday', type: 'schedule' },
      { user: contributors[0], action: 'flagged resource constraint issue', time: 'Yesterday', type: 'flag' },
      { user: owner, action: 'approved milestone completion', time: 'Yesterday', type: 'complete' },
    ],
    earlier: [
      { user: contributors[0], action: 'uploaded final design assets', time: '3 days ago', type: 'add' },
      { user: owner, action: 'updated collaboration priority to High', time: '4 days ago', type: 'edit' },
      { user: contributors[1] || owner, action: 'completed initial research phase', time: '5 days ago', type: 'complete' },
      { user: owner, action: 'created this collaboration', time: '2 weeks ago', type: 'add' },
    ]
  };
  
  const mockMetrics = {
    tasksCompleted: Math.floor(Math.random() * 12) + 8,
    tasksTotal: Math.floor(Math.random() * 8) + 25,
    hoursLogged: Math.floor(Math.random() * 40) + 60,
    commentsCount: Math.floor(Math.random() * 25) + 20
  };

  const statusConfigs = {
    'Active': { bg: `${DESIGN.colors.primary}20`, text: DESIGN.colors.primary, glow: `0 0 25px ${DESIGN.colors.primary}50` },
    'In Review': { bg: 'rgba(59, 130, 246, 0.2)', text: '#2563EB', glow: '0 0 25px rgba(59, 130, 246, 0.5)' },
    'Completed': { bg: `${DESIGN.colors.success}20`, text: DESIGN.colors.success, glow: '0 0 25px rgba(16, 185, 129, 0.5)' },
    'Planned': { bg: DESIGN.colors.background, text: DESIGN.colors.textSecondary, glow: 'none' }
  };
  const statusConfig = statusConfigs[task.status] || statusConfigs.Planned;
  
  // Activity type icons and colors
  const activityTypeConfig = {
    complete: { icon: CheckCircle2, color: DESIGN.colors.success },
    edit: { icon: Pencil, color: DESIGN.colors.blue },
    add: { icon: Plus, color: DESIGN.colors.primary },
    schedule: { icon: CalendarDays, color: '#8B5CF6' },
    flag: { icon: Flag, color: DESIGN.colors.amber }
  };

  // Contributor lane mapping (mock)
  const getContributorLane = (contributor) => {
    const lanes = ['creative', 'marketing', 'execution', 'operations', 'revenue'];
    const idx = contributor?.name?.charCodeAt(0) % lanes.length || 0;
    return getLane(lanes[idx]);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="collab-detail-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      
      <div 
        className="relative w-full max-w-2xl shadow-2xl overflow-y-auto animate-slide-in"
        style={{ backgroundColor: DESIGN.colors.card }}
      >
        {/* Premium Header with Lane Gradient Wash */}
        <div 
          className="sticky top-0 z-10 p-6"
          style={{ 
            background: `linear-gradient(180deg, ${primaryLane?.color}15 0%, ${primaryLane?.color}08 50%, ${DESIGN.colors.card} 100%)`,
            borderBottom: `1px solid ${DESIGN.colors.border}`
          }}
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <span 
                  className="text-xs font-black px-4 py-2 rounded-xl uppercase tracking-wide"
                  style={{ backgroundColor: statusConfig.bg, color: statusConfig.text, boxShadow: statusConfig.glow }}
                >
                  {task.status}
                </span>
                <span 
                  className="text-xs font-bold px-3 py-2 rounded-xl flex items-center gap-1.5"
                  style={{ backgroundColor: `${levelColor}20`, color: levelColor }}
                >
                  <Layers className="w-3.5 h-3.5" />
                  {task.required_level}
                </span>
                <CollabBadge size="sm" />
              </div>
              <h2 className="text-2xl font-black leading-tight" style={{ color: DESIGN.colors.textPrimary }}>
                {task.title}
              </h2>
              <p className="text-sm mt-2 leading-relaxed" style={{ color: DESIGN.colors.textSecondary }}>
                {task.description}
              </p>
            </div>
            <button 
              onClick={onClose} 
              className="p-2.5 rounded-xl hover:bg-black/5 transition-colors ml-4"
            >
              <X className="w-6 h-6" style={{ color: DESIGN.colors.textMuted }} />
            </button>
          </div>
          
          {/* Premium Progress Bar */}
          <div className="mt-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold uppercase tracking-wider" style={{ color: DESIGN.colors.textMuted }}>
                Overall Progress
              </span>
              <span className="text-base font-black" style={{ color: primaryLane?.color }}>{mockProgress}%</span>
            </div>
            <div className="h-3 rounded-full overflow-hidden" style={{ backgroundColor: '#E5E7EB' }}>
              <div 
                className="h-full rounded-full relative"
                style={{ 
                  width: `${mockProgress}%`,
                  background: `linear-gradient(90deg, ${primaryLane?.color} 0%, ${primaryLane?.color}DD 50%, ${primaryLane?.color}BB 100%)`,
                  boxShadow: `0 0 20px ${primaryLane?.color}60`
                }}
              >
                {/* Glow at leading edge */}
                <div 
                  className="absolute right-0 top-0 bottom-0 w-8"
                  style={{ background: `linear-gradient(90deg, transparent, ${primaryLane?.color})` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Premium Pill Tab Navigation */}
        <div 
          className="px-6 pt-4"
          style={{ backgroundColor: DESIGN.colors.background }}
        >
          <div className="inline-flex p-1.5 rounded-2xl" style={{ backgroundColor: DESIGN.colors.card }}>
            {[
              { value: 'overview', icon: Target, label: 'Overview' },
              { value: 'team', icon: Users, label: 'Team' },
              { value: 'milestones', icon: ListChecks, label: 'Milestones' },
              { value: 'activity', icon: Activity, label: 'Activity' }
            ].map(tab => (
              <button
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300"
                style={{
                  backgroundColor: activeTab === tab.value ? DESIGN.colors.primary : 'transparent',
                  color: activeTab === tab.value ? 'white' : DESIGN.colors.textSecondary,
                  boxShadow: activeTab === tab.value ? DESIGN.shadows.glowPrimary : 'none'
                }}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* Premium Four-Up Stats */}
              <div className="grid grid-cols-4 gap-4">
                {[
                  { icon: CheckCircle2, value: `${mockMetrics.tasksCompleted}/${mockMetrics.tasksTotal}`, label: 'Tasks', color: DESIGN.colors.success },
                  { icon: Clock, value: `${mockMetrics.hoursLogged}h`, label: 'Logged', color: DESIGN.colors.blue },
                  { icon: MessageSquare, value: mockMetrics.commentsCount, label: 'Comments', color: '#8B5CF6' },
                  { icon: Users, value: contributors.length + 1, label: 'Members', color: DESIGN.colors.primary }
                ].map((stat, idx) => (
                  <div key={idx} className="relative text-center">
                    <div 
                      className="w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center"
                      style={{ backgroundColor: `${stat.color}15`, boxShadow: `0 0 15px ${stat.color}25` }}
                    >
                      <stat.icon className="w-6 h-6" style={{ color: stat.color }} />
                    </div>
                    <p className="text-2xl font-black" style={{ color: DESIGN.colors.textPrimary }}>{stat.value}</p>
                    <p className="text-xs font-bold uppercase tracking-wider mt-1" style={{ color: DESIGN.colors.textMuted }}>{stat.label}</p>
                    {idx < 3 && (
                      <div 
                        className="absolute right-0 top-1/2 -translate-y-1/2 w-px h-16"
                        style={{ backgroundColor: DESIGN.colors.border }}
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* Cognitive Composition Section - ELEVATED */}
              <div 
                className="rounded-2xl p-6"
                style={{ 
                  backgroundColor: DESIGN.colors.background,
                  border: `1px solid ${DESIGN.colors.border}`
                }}
              >
                <div className="flex items-center gap-2 mb-5">
                  <h4 className="text-sm font-black uppercase tracking-wider" style={{ color: DESIGN.colors.textPrimary }}>
                    Cognitive Composition
                  </h4>
                  <div className="relative group">
                    <Info className="w-4 h-4 cursor-help" style={{ color: DESIGN.colors.textMuted }} />
                    <div 
                      className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 rounded-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-20"
                      style={{ backgroundColor: DESIGN.colors.card, boxShadow: DESIGN.shadows.xl }}
                    >
                      <p className="text-xs leading-relaxed" style={{ color: DESIGN.colors.textSecondary }}>
                        Shows the cognitive lanes involved in this collaboration. The Primary lane drives the work, while supporting lanes contribute specialized thinking modes.
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Primary Lane Card */}
                <div 
                  className="rounded-2xl p-5 mb-4"
                  style={{ 
                    background: `linear-gradient(135deg, ${primaryLane?.color} 0%, ${primaryLane?.color}DD 100%)`,
                    boxShadow: `0 8px 30px ${primaryLane?.color}40`
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs font-bold px-2.5 py-1 rounded-lg bg-white/20 text-white mb-2 inline-block">
                        Primary
                      </span>
                      <h5 className="text-xl font-black text-white mt-2">{primaryLane?.name}</h5>
                      <p className="text-sm text-white/80 mt-1">Drives the core direction and decision-making for this collaboration</p>
                    </div>
                    <div 
                      className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center"
                      style={{ boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }}
                    >
                      <Target className="w-8 h-8 text-white" />
                    </div>
                  </div>
                </div>
                
                {/* Supporting Lanes */}
                {supportingLanes.length > 0 && (
                  <div className="grid grid-cols-2 gap-3">
                    {supportingLanes.map(lane => (
                      <div 
                        key={lane.id}
                        className="p-4 rounded-xl"
                        style={{ 
                          backgroundColor: DESIGN.colors.card,
                          borderLeft: `4px solid ${lane.color}`
                        }}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: lane.color }}
                          />
                          <span className="font-bold text-sm" style={{ color: DESIGN.colors.textPrimary }}>{lane.name}</span>
                        </div>
                        <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>Supporting lane</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Due Date & Level - Premium Info Cards */}
              <div className="grid grid-cols-2 gap-4">
                <div 
                  className="p-5 rounded-2xl"
                  style={{ backgroundColor: DESIGN.colors.background, border: `1px solid ${DESIGN.colors.border}` }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div 
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${DESIGN.colors.blue}15` }}
                    >
                      <Calendar className="w-5 h-5" style={{ color: DESIGN.colors.blue }} />
                    </div>
                    <span className="text-xs font-bold uppercase tracking-wider" style={{ color: DESIGN.colors.textMuted }}>Due Date</span>
                  </div>
                  <p className="text-xl font-black" style={{ color: DESIGN.colors.textPrimary }}>
                    {new Date(task.due_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
                <div 
                  className="p-5 rounded-2xl"
                  style={{ backgroundColor: DESIGN.colors.background, border: `1px solid ${DESIGN.colors.border}` }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div 
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${levelColor}15` }}
                    >
                      <Layers className="w-5 h-5" style={{ color: levelColor }} />
                    </div>
                    <span className="text-xs font-bold uppercase tracking-wider" style={{ color: DESIGN.colors.textMuted }}>Required Level</span>
                  </div>
                  <p className="text-xl font-black" style={{ color: levelColor }}>
                    {task.required_level}
                  </p>
                  <p className="text-sm mt-1" style={{ color: DESIGN.colors.textSecondary }}>
                    {levelData?.description || 'Strategic decision level'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Team Tab - ELEVATED */}
          {activeTab === 'team' && (
            <div className="space-y-6">
              {/* Owner Card - Premium Treatment */}
              <div>
                <h4 className="text-xs font-black uppercase tracking-wider mb-4" style={{ color: DESIGN.colors.textMuted }}>
                  Owner
                </h4>
                <div 
                  className="p-5 rounded-2xl"
                  style={{ 
                    background: `linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(251, 146, 60, 0.08) 100%)`,
                    border: '2px solid rgba(245, 158, 11, 0.25)'
                  }}
                >
                  <div className="flex items-center gap-5">
                    <PremiumAvatar member={owner} isOwner={true} size="lg" showCapacity={true} />
                    <div className="flex-1">
                      <p className="text-xl font-black" style={{ color: DESIGN.colors.textPrimary }}>{owner?.name}</p>
                      <p className="text-sm" style={{ color: DESIGN.colors.textSecondary }}>{owner?.title}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Crown className="w-4 h-4" style={{ color: '#D97706' }} />
                        <span className="text-xs font-bold" style={{ color: '#D97706' }}>Project Owner</span>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="rounded-xl"
                      style={{ borderColor: DESIGN.colors.primary, color: DESIGN.colors.primary }}
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Message
                    </Button>
                  </div>
                </div>
              </div>

              {/* Contributors */}
              <div>
                <h4 className="text-xs font-black uppercase tracking-wider mb-4" style={{ color: DESIGN.colors.textMuted }}>
                  Contributors ({contributors.length})
                </h4>
                <div className="space-y-3">
                  {contributors.map(contributor => {
                    const contributorLane = getContributorLane(contributor);
                    return (
                      <div 
                        key={contributor?.id}
                        className="p-4 rounded-xl flex items-center gap-4 group hover:shadow-lg transition-all cursor-pointer"
                        style={{ backgroundColor: DESIGN.colors.background }}
                      >
                        <PremiumAvatar member={contributor} isOwner={false} size="md" showCapacity={true} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-bold" style={{ color: DESIGN.colors.textPrimary }}>{contributor?.name}</p>
                            {/* Lane indicator */}
                            <div 
                              className="flex items-center gap-1.5 px-2 py-0.5 rounded-lg"
                              style={{ backgroundColor: `${contributorLane?.color}15` }}
                            >
                              <div 
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: contributorLane?.color }}
                              />
                              <span className="text-xs font-medium" style={{ color: contributorLane?.color }}>
                                {contributorLane?.name}
                              </span>
                            </div>
                          </div>
                          <p className="text-sm" style={{ color: DESIGN.colors.textSecondary }}>{contributor?.title}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-black" style={{ color: DESIGN.colors.primary }}>
                            {Math.floor(Math.random() * 8) + 3}
                          </p>
                          <p className="text-xs font-medium" style={{ color: DESIGN.colors.textMuted }}>tasks</p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="rounded-xl opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <MessageSquare className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })}
                  {contributors.length === 0 && (
                    <div className="text-center py-8" style={{ color: DESIGN.colors.textMuted }}>
                      <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">No additional contributors yet</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Milestones Tab - ELEVATED */}
          {activeTab === 'milestones' && (
            <div className="relative">
              {/* Timeline Line */}
              <div 
                className="absolute left-[22px] top-10 bottom-10 w-0.5"
                style={{ backgroundColor: DESIGN.colors.border }}
              />
              
              <div className="space-y-5">
                {mockMilestones.map((milestone, idx) => {
                  const statusStyles = {
                    completed: { 
                      color: DESIGN.colors.success, 
                      bg: DESIGN.colors.successSubtle, 
                      icon: CheckCircle2,
                      iconBg: DESIGN.colors.success,
                      glow: `0 0 15px ${DESIGN.colors.success}60`
                    },
                    in_progress: { 
                      color: DESIGN.colors.primary, 
                      bg: DESIGN.colors.primarySubtle, 
                      icon: CircleDot,
                      iconBg: DESIGN.colors.primary,
                      glow: `0 0 20px ${DESIGN.colors.primary}60`,
                      pulse: true
                    },
                    pending: { 
                      color: DESIGN.colors.textMuted, 
                      bg: DESIGN.colors.background, 
                      icon: Clock,
                      iconBg: DESIGN.colors.border,
                      glow: 'none'
                    }
                  };
                  const style = statusStyles[milestone.status];
                  const Icon = style.icon;
                  
                  return (
                    <div key={idx} className="flex items-start gap-5 relative">
                      {/* Timeline indicator */}
                      <div 
                        className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 z-10 ${style.pulse ? 'animate-pulse' : ''}`}
                        style={{ 
                          backgroundColor: milestone.status === 'completed' ? style.iconBg : style.bg,
                          boxShadow: style.glow,
                          border: milestone.status === 'in_progress' ? `3px solid ${style.color}` : 'none'
                        }}
                      >
                        <Icon 
                          className="w-5 h-5" 
                          style={{ color: milestone.status === 'completed' ? 'white' : style.color }} 
                        />
                      </div>
                      
                      {/* Milestone card */}
                      <div 
                        className={`flex-1 p-5 rounded-2xl ${milestone.status === 'in_progress' ? 'shadow-lg' : ''}`}
                        style={{ 
                          backgroundColor: milestone.status === 'in_progress' ? DESIGN.colors.card : DESIGN.colors.background,
                          border: milestone.status === 'in_progress' ? `2px solid ${style.color}30` : `1px solid ${DESIGN.colors.border}`,
                          background: milestone.status === 'in_progress' 
                            ? `linear-gradient(135deg, ${style.color}08 0%, ${DESIGN.colors.card} 100%)` 
                            : undefined
                        }}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-bold text-base" style={{ color: DESIGN.colors.textPrimary }}>{milestone.name}</h5>
                          <span className="text-xs font-semibold" style={{ color: DESIGN.colors.textMuted }}>{milestone.date}</span>
                        </div>
                        <p className="text-sm mb-3" style={{ color: DESIGN.colors.textSecondary }}>{milestone.description}</p>
                        
                        {/* Assignee */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <PremiumAvatar member={milestone.assignee} size="sm" showCapacity={false} />
                            <span className="text-xs font-medium" style={{ color: DESIGN.colors.textMuted }}>
                              {milestone.assignee?.name}
                            </span>
                          </div>
                          
                          {milestone.status === 'in_progress' && milestone.progress && (
                            <div className="flex items-center gap-3">
                              <div className="w-24 h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#E5E7EB' }}>
                                <div 
                                  className="h-full rounded-full"
                                  style={{ 
                                    width: `${milestone.progress}%`, 
                                    background: `linear-gradient(90deg, ${style.color} 0%, ${style.color}BB 100%)`,
                                    boxShadow: `0 0 10px ${style.color}50`
                                  }}
                                />
                              </div>
                              <span className="text-xs font-black" style={{ color: style.color }}>{milestone.progress}%</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Activity Tab - EXPANDED & ELEVATED */}
          {activeTab === 'activity' && (
            <div className="space-y-6">
              {Object.entries(mockActivity).map(([dateGroup, activities]) => (
                <div key={dateGroup}>
                  {/* Date separator */}
                  <div className="flex items-center gap-3 mb-4">
                    <span 
                      className="text-xs font-black uppercase tracking-wider"
                      style={{ color: DESIGN.colors.textMuted }}
                    >
                      {dateGroup === 'today' ? 'Today' : dateGroup === 'yesterday' ? 'Yesterday' : 'Earlier This Week'}
                    </span>
                    <div className="flex-1 h-px" style={{ backgroundColor: DESIGN.colors.border }} />
                  </div>
                  
                  <div className="space-y-3">
                    {activities.map((activity, idx) => {
                      const config = activityTypeConfig[activity.type] || activityTypeConfig.edit;
                      const Icon = config.icon;
                      
                      return (
                        <div 
                          key={idx}
                          className="flex items-start gap-4 p-4 rounded-xl hover:shadow-md transition-all"
                          style={{ backgroundColor: DESIGN.colors.background }}
                        >
                          <div 
                            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${config.color}15` }}
                          >
                            <Icon className="w-5 h-5" style={{ color: config.color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm" style={{ color: DESIGN.colors.textPrimary }}>
                              <span className="font-bold" style={{ color: DESIGN.colors.primary }}>
                                {activity.user?.name}
                              </span>
                              {' '}{activity.action}
                            </p>
                            <p className="text-xs mt-1" style={{ color: DESIGN.colors.textMuted }}>{activity.time}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Elevated Footer Actions */}
        <div 
          className="sticky bottom-0 p-6 border-t flex items-center justify-between"
          style={{ backgroundColor: DESIGN.colors.card, borderColor: DESIGN.colors.border }}
        >
          <Button 
            variant="outline" 
            className="rounded-xl gap-2"
            style={{ borderColor: DESIGN.colors.border }}
          >
            <MessageSquare className="w-4 h-4" />
            Start Discussion
          </Button>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="rounded-xl gap-2"
              style={{ borderColor: primaryLane?.color, color: primaryLane?.color }}
            >
              <ArrowUpRight className="w-4 h-4" />
              Open Full View
            </Button>
            
            {/* Action Dropdown */}
            <div className="relative">
              <Button 
                className="rounded-xl gap-2"
                style={{ 
                  background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
                  boxShadow: DESIGN.shadows.glowPrimary
                }}
                onClick={() => setShowActionDropdown(!showActionDropdown)}
              >
                <Sparkles className="w-4 h-4" />
                Take Action
                <ChevronDown className={`w-4 h-4 transition-transform ${showActionDropdown ? 'rotate-180' : ''}`} />
              </Button>
              
              {showActionDropdown && (
                <div 
                  className="absolute bottom-full right-0 mb-2 w-48 rounded-xl overflow-hidden shadow-xl z-20"
                  style={{ backgroundColor: DESIGN.colors.card }}
                >
                  {[
                    { icon: Plus, label: 'Add Task' },
                    { icon: Users, label: 'Assign Member' },
                    { icon: Edit3, label: 'Update Status' },
                    { icon: CalendarDays, label: 'Schedule Meeting' }
                  ].map((action, idx) => (
                    <button
                      key={idx}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium hover:bg-gray-50 transition-colors"
                      style={{ color: DESIGN.colors.textPrimary }}
                      onClick={() => setShowActionDropdown(false)}
                    >
                      <action.icon className="w-4 h-4" style={{ color: DESIGN.colors.primary }} />
                      {action.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .animate-slide-in {
          animation: slideIn 0.3s ease-out;
        }
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

// ================================================
// PREMIUM COLLABORATION CARD - ELEVATED
// ================================================
const PremiumCollabCard = ({ task, index, onClick }) => {
  const owner = getOwner(task.owner_id);
  const contributors = getContributors(task.contributors);
  const allMembers = [owner, ...contributors].filter(Boolean);
  const primaryLane = getLane(task.primary_lane);
  const supportingLanes = task.supporting_lanes?.map(lId => getLane(lId)).filter(Boolean) || [];
  const levelColor = getLevelColor(task.required_level);
  
  const mockProgress = useMemo(() => Math.floor(Math.random() * 50) + 40, [task.id]);

  const statusConfigs = {
    'Active': { bg: `${DESIGN.colors.primary}12`, text: DESIGN.colors.primary, glow: `0 0 20px ${DESIGN.colors.primary}35` },
    'In Review': { bg: 'rgba(59, 130, 246, 0.12)', text: '#2563EB', glow: '0 0 20px rgba(59, 130, 246, 0.35)' },
    'Completed': { bg: `${DESIGN.colors.success}12`, text: DESIGN.colors.success, glow: '0 0 20px rgba(16, 185, 129, 0.35)' },
    'Planned': { bg: DESIGN.colors.background, text: DESIGN.colors.textSecondary, glow: 'none' }
  };
  const statusConfig = statusConfigs[task.status] || statusConfigs.Planned;

  // Multi-lane gradient for left border
  const borderGradient = getMultiLaneGradient(primaryLane, supportingLanes);

  return (
    <div 
      onClick={onClick}
      className="relative rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 cursor-pointer group"
      style={{
        backgroundColor: DESIGN.colors.card,
        boxShadow: DESIGN.shadows.lg,
        animation: `fadeInUp 0.5s ease-out ${index * 100}ms forwards`,
        opacity: 0
      }}
      data-testid={`collab-card-${task.id}`}
    >
      {/* Multi-lane gradient left border - 4px width */}
      <div 
        className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl"
        style={{ background: borderGradient }}
      />
      
      {/* Hover gradient overlay */}
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ background: `linear-gradient(135deg, ${primaryLane?.color}08 0%, transparent 60%)` }}
      />
      
      <div className="relative p-6 pl-7">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 min-w-0 pr-3">
            <h3 
              className="text-lg font-black mb-1.5 group-hover:text-teal-600 transition-colors leading-tight"
              style={{ color: DESIGN.colors.textPrimary }}
            >
              {task.title}
            </h3>
            <p className="text-sm line-clamp-2 leading-relaxed" style={{ color: DESIGN.colors.textSecondary }}>
              {task.description}
            </p>
          </div>
          <span 
            className="flex-shrink-0 text-xs font-bold px-3.5 py-2 rounded-xl"
            style={{ backgroundColor: statusConfig.bg, color: statusConfig.text, boxShadow: statusConfig.glow }}
          >
            {task.status}
          </span>
        </div>

        {/* Premium Progress Bar */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: DESIGN.colors.textMuted }}>Progress</span>
            <span className="text-sm font-black" style={{ color: primaryLane?.color }}>{mockProgress}%</span>
          </div>
          <div className="h-2.5 rounded-full overflow-hidden" style={{ backgroundColor: '#E5E7EB' }}>
            <div 
              className="h-full rounded-full relative"
              style={{ 
                width: `${mockProgress}%`,
                background: `linear-gradient(90deg, ${primaryLane?.color} 0%, ${primaryLane?.color}CC 100%)`,
                boxShadow: `0 0 15px ${primaryLane?.color}50`
              }}
            >
              {/* Glow at leading edge */}
              <div 
                className="absolute right-0 top-0 bottom-0 w-4"
                style={{ background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.4))` }}
              />
            </div>
          </div>
        </div>

        {/* Team Avatars - Premium Overlapping Cluster */}
        <div className="mb-5">
          <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: DESIGN.colors.textMuted }}>
            Team
          </p>
          <AvatarCluster members={allMembers} maxDisplay={4} size="sm" />
        </div>

        {/* Lanes - Elevated Treatment */}
        <div className="flex items-center gap-2 flex-wrap mb-5">
          <PrimaryLanePill lane={primaryLane} />
          {supportingLanes.slice(0, 2).map(lane => (
            <SupportingLanePill key={lane.id} lane={lane} />
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t" style={{ borderColor: DESIGN.colors.borderSubtle }}>
          <div className="flex items-center gap-4 text-xs" style={{ color: DESIGN.colors.textMuted }}>
            <span className="flex items-center gap-1.5 font-medium">
              <Calendar className="w-3.5 h-3.5" />
              {new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
            <span 
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-bold"
              style={{ backgroundColor: `${levelColor}15`, color: levelColor }}
            >
              <Layers className="w-3 h-3" />
              {task.required_level}
            </span>
          </div>
          <div 
            className="flex items-center gap-1.5 text-sm font-bold opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-1"
            style={{ color: DESIGN.colors.primary }}
          >
            View Details
            <ChevronRight className="w-4 h-4" />
          </div>
        </div>
      </div>
    </div>
  );
};

// ================================================
// MAIN COLLAB PAGE
// ================================================
const Collab = () => {
  const [selectedTask, setSelectedTask] = useState(null);
  const [showNewCollabModal, setShowNewCollabModal] = useState(false);
  
  const collabTasks = tasks.filter(t => 
    t.contributors.length >= 1 || t.supporting_lanes?.length > 0
  );

  const stats = [
    { icon: Users, label: 'Active Collaborations', value: collabTasks.length, color: DESIGN.colors.primary },
    { icon: GitBranch, label: 'Multi-Lane Tasks', value: collabTasks.filter(t => t.supporting_lanes?.length > 0).length, color: '#8B5CF6' },
    { icon: Zap, label: 'Contributors Active', value: new Set(collabTasks.flatMap(t => t.contributors)).size, color: '#F59E0B' }
  ];

  return (
    <div className="space-y-8" data-testid="collab-page">
      {/* Premium Page Header */}
      <div className="relative">
        <div 
          className="absolute inset-0 rounded-3xl opacity-30"
          style={{ background: 'linear-gradient(135deg, rgba(0, 201, 167, 0.12) 0%, rgba(139, 92, 246, 0.08) 50%, rgba(245, 158, 11, 0.06) 100%)' }}
        />
        <div className="relative flex items-center justify-between py-6">
          <div className="flex items-center gap-4">
            <div 
              className="w-14 h-14 rounded-2xl flex items-center justify-center"
              style={{ 
                background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
                boxShadow: DESIGN.shadows.glowPrimary
              }}
            >
              <Users className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-black tracking-tight" style={{ color: DESIGN.colors.textPrimary }}>
                COLLAB
              </h1>
              <p className="text-base" style={{ color: DESIGN.colors.textSecondary }}>
                Multi-assignee coordination and cross-lane collaboration intelligence
              </p>
            </div>
          </div>
          
          <Button 
            className="rounded-xl gap-2 px-6 py-3 text-base font-bold"
            style={{ 
              background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
              boxShadow: DESIGN.shadows.glowPrimary
            }}
            onClick={() => setShowNewCollabModal(true)}
            data-testid="new-collaboration-btn"
          >
            <Sparkles className="w-5 h-5" />
            New Collaboration
          </Button>
        </div>
      </div>

      {/* Premium Elevated Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {stats.map((stat, idx) => (
          <PremiumStatCard key={idx} index={idx} {...stat} />
        ))}
      </div>

      {/* Collaboration Cards */}
      <div className="space-y-6">
        <h2 className="text-lg font-black flex items-center gap-3" style={{ color: DESIGN.colors.textPrimary }}>
          <Target className="w-5 h-5" style={{ color: DESIGN.colors.primary }} />
          Active Collaborations
          <span 
            className="text-sm font-bold px-4 py-1.5 rounded-xl"
            style={{ backgroundColor: DESIGN.colors.primarySubtle, color: DESIGN.colors.primary }}
          >
            {collabTasks.length} total
          </span>
        </h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {collabTasks.map((task, index) => (
            <PremiumCollabCard 
              key={task.id} 
              task={task} 
              index={index} 
              onClick={() => setSelectedTask(task)}
            />
          ))}
        </div>
      </div>

      {/* Detail Modal */}
      {selectedTask && (
        <CollabDetailModal 
          task={selectedTask} 
          onClose={() => setSelectedTask(null)} 
        />
      )}

      {/* New Collaboration Modal */}
      <NewCollaborationModal 
        isOpen={showNewCollabModal}
        onClose={() => setShowNewCollabModal(false)}
        onSuccess={() => setShowNewCollabModal(false)}
      />

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

// Export components for use in Tasks page
export { 
  CollabBadge, 
  LaneCompositionDots, 
  AvatarCluster, 
  getMultiLaneGradient,
  CollabDetailModal,
  PremiumAvatar 
};
export default Collab;
